import type { ExtendedOptions } from '@cdc-internal/fe-toolbox/libs/fetcher'
import { queryOptions, useSuspenseQuery } from '@tanstack/react-query'

import {
  memberFetcherV2,
  metaFetcherV2,
  payloadCmsFetcher,
  popularFetcher,
  priceFetcher,
  priceFetcherV2,
} from '@/libs/fetcher/presets'
import {
  CoinFromAll,
  CoinId,
  CoinMarket,
  CoinName,
  CoinOverall,
  CoinSymbol,
  PopularCoin,
  TopMoverItem,
  TrendingCoin,
  VisitType,
} from '@/types/coin'

export const fetchAllCoins = (options?: ExtendedOptions) =>
  metaFetcherV2<CoinFromAll[]>('/all-tokens', undefined, options as ExtendedOptions<CoinFromAll[]>)

export const allCoinsQueryOptions = () =>
  queryOptions({
    queryKey: ['allCoins'],
    queryFn: () => fetchAllCoins(),
  })

export interface FetchCoinsPayload {
  page: number
  limit: number
  tags?: string
  sort?: string
  direction?: 'DESC' | 'ASC'
}

type CoinFieldsInGetList =
  | 'active'
  | 'app_symbol'
  | 'app_tradable'
  | 'id'
  | 'name'
  | 'prices'
  | 'rank'
  | 'slug'
  | 'symbol'
  | 'token_id'
  | 'usd_marketcap'
  | 'usd_price'
  | 'usd_price_change_24h'
  | 'usd_volume_24h'

export type CoinItemInGetList = Pick<CoinOverall, CoinFieldsInGetList>

export interface FetchCoinsResponse {
  data: CoinItemInGetList[]
  total: number
}

const fetchCoins = (queries: FetchCoinsPayload) =>
  priceFetcher<FetchCoinsResponse>(
    '/tokens',
    {
      queries: {
        ...queries,
      },
    },
    {
      successResponseDataProperty: '',
    },
  ).then(res => {
    // Clear unused prices to save memory
    res.data.forEach(coin => {
      coin.prices = []
    })
    return res
  })

export const coinsQueryOptions = (payload: FetchCoinsPayload) =>
  queryOptions({
    queryKey: ['coins', payload],
    queryFn: () => fetchCoins(payload),
  })

export const useCoins = (payload: FetchCoinsPayload) => useSuspenseQuery(coinsQueryOptions(payload))

export enum TradablePlatform {
  APP = 'APP',
  APP_OR_EXCHANGE = 'EXCHANGE-OR-APP',
  EXCHANGE = 'EXCHANGE',
}

export const enum TopMoversDirection {
  ASC = '1',
  DESC = '-1',
}

interface FetchTopMoversPayload {
  depth?: number
  direction?: TopMoversDirection
  tradable_on?: TradablePlatform
  tag?: string
}

export const fetchTopMovers = (payload: FetchTopMoversPayload) =>
  priceFetcher<TopMoverItem[]>('/top-movers', {
    queries: {
      ...payload,
    },
  }).then(res => {
    // Clear unused prices to save memory
    res.forEach(coin => {
      coin.prices = []
    })
    return res
  })

export const topMoversQueryOptions = (payload: FetchTopMoversPayload) =>
  queryOptions({
    queryKey: ['topMovers', payload],
    queryFn: () => fetchTopMovers(payload),
  })

export const useTopMovers = (payload: FetchTopMoversPayload) =>
  useSuspenseQuery(topMoversQueryOptions(payload))

const fetchTrendingCoins = (limit: number) =>
  priceFetcher<TrendingCoin[]>('/trending-tokens', {
    queries: { limit },
  })

export const trendingCoinQueryOptions = (limit: number) =>
  queryOptions({
    queryKey: ['trendingCoins', limit],
    queryFn: () => fetchTrendingCoins(limit),
  })

const fetchTopWatchList = () => memberFetcherV2<CoinId[]>('/watchitem/hot')

export const topWatchListQueryOptions = queryOptions({
  queryKey: ['topWatchList'],
  queryFn: () => fetchTopWatchList(),
})

const fetchPopularCoins = (type: VisitType) =>
  popularFetcher<PopularCoin[]>('/most-visited', {
    queries: {
      type,
    },
  })

export const popularCoinsQueryOptions = (type: VisitType) =>
  queryOptions({
    queryKey: ['popularCoins', type],
    queryFn: () => fetchPopularCoins(type),
    refetchOnMount: false,
  })

export type CoinItemInPostList = Omit<
  CoinMarket,
  'price_update_time' | 'isPriceAlive' | 'app_symbol'
> & {
  name: CoinName
  symbol: CoinSymbol
}

const fetchCoinsByIds = (coinIds: CoinId[]) =>
  priceFetcherV2<CoinItemInPostList[]>('/tokens', {
    method: 'POST',
    body: {
      token_ids: coinIds,
    },
  })

export const coinsByIdsQueryOptions = (coinIds: CoinId[]) =>
  queryOptions({
    queryKey: ['coins', coinIds],
    queryFn: () => fetchCoinsByIds(coinIds),
    enabled: coinIds.length > 0,
  })

export const useCoinsByIds = (coinIds: CoinId[]) =>
  useSuspenseQuery(coinsByIdsQueryOptions(coinIds))

export const fetchCmsTrendingCoins = () =>
  payloadCmsFetcher<string[]>(`/price-featured-token`, undefined, {
    customData: { enableDefaultNextjsServerFetchCache: true },
  })

export const cmsTrendingCoinsQueryOptions = () =>
  queryOptions({
    queryKey: ['cmsTrendingCoins'],
    queryFn: () => fetchCmsTrendingCoins(),
  })

export const useCmsTrendingCoins = () => {
  return useSuspenseQuery(cmsTrendingCoinsQueryOptions())
}
